interface AppConfig {
  CLIENT: string
  CORE_TYPE: string
  TITLE: string
  LOGO_TITLE: string
  LOGO_SUBTITLE: string
  THEME_PRIMARY: string
  THEME_SECONDARY: string
  THEME_ACCENT: string
  API_BASE_URL: string
  MODE: string
  IS_DEVELOPMENT: boolean
  IS_PRODUCTION: boolean
}

class ConfigService {
  private config: AppConfig

  constructor() {
    this.config = this.loadConfig()
  }

  private loadConfig(): AppConfig {
    const mode = import.meta.env.MODE || 'development'

    return {
      CLIENT: import.meta.env.VITE_CLIENT || 'default',
      CORE_TYPE: import.meta.env.VITE_CORE_TYPE || 'WF',
      TITLE: import.meta.env.VITE_TITLE || 'ESOFT',
      LOGO_TITLE: import.meta.env.VITE_LOGO_TITLE || 'ESOFT',
      LOGO_SUBTITLE: import.meta.env.VITE_LOGO_SUBTITLE,
      THEME_PRIMARY: import.meta.env.VITE_THEME_PRIMARY || '#1E3A6E',
      THEME_SECONDARY: import.meta.env.VITE_THEME_SECONDARY || '#42A5F5',
      THEME_ACCENT: import.meta.env.VITE_THEME_ACCENT || import.meta.env.VITE_THEME_PRIMARY || '#1E3A6E',
      API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api',
      MODE: mode,
      IS_DEVELOPMENT: mode === 'development',
      IS_PRODUCTION: mode === 'production'
    }
  }

  reload() {
    this.config = this.loadConfig()
  }

  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.config[key]
  }

  getAll(): AppConfig {
    return { ...this.config }
  }

  // Хелперы для проверки клиента
  isMetroSpb(): boolean {
    return this.config.CLIENT === 'metro-spb'
  }

  isRkn(): boolean {
    return this.config.CLIENT === 'rkn'
  }

  isRfc(): boolean {
    return this.config.CLIENT === 'rfc'
  }

  isSpd(): boolean {
    return this.config.CLIENT === 'spd'
  }

  /** Dev / test Vite modes; hide when MODE contains `production`. */
  isNonProductionMode(): boolean {
    return !this.config.MODE.includes('production')
  }

  // Получить путь к логотипу (используем динамические импорты)
  async getLogoPath(): Promise<string> {
    const client = this.config.CLIENT

    try {
      if (client === 'metro-spb') {
        const logo = await import('@/img/spb-head-logo.svg')
        return logo.default
      } else if (client === 'rkn') {
        const logo = await import('@/img/rkn-logo.svg')
        return logo.default
      } else if (client === 'rfc') {
        const logo = await import('@/img/rfc-logo.svg')
        return logo.default
      } else if (client === 'spd') {
        const logo = await import('@/img/spd-logo.svg')
        return logo.default
      } else {
        const logo = await import('@/img/head-logo.png')
        return logo.default
      }
    } catch (error) {
      console.warn('Logo not found, using default', error)
      // Fallback логотип
      const logo = await import('@/img/head-logo.png')
      return logo.default
    }
  }

  // Синхронная версия для computed свойств - используем статические пути
  getLogoPathSync(): string {
    const client = this.config.CLIENT

    if (client === 'metro-spb') {
      return new URL('@/img/spb-head-logo.svg', import.meta.url).href
    } else if (client === 'rkn') {
      return new URL('@/img/rkn-logo.svg', import.meta.url).href
    } else if (client === 'rfc') {
      return new URL('@/img/rfc-logo.svg', import.meta.url).href
    } else if (client === 'spd') {
      return new URL('@/img/spd-logo.svg', import.meta.url).href
    } else {
      return new URL('@/img/head-logo.png', import.meta.url).href
    }
  }

  // Получить настройки темы для заголовка
  getHeaderStyles() {
    const primary = this.config.THEME_PRIMARY
    const secondary = this.config.THEME_SECONDARY

    return {
      background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
      borderBottom: `1px solid ${primary}`,
    }
  }

  applyClientThemeToDocument() {
    const { CLIENT, THEME_PRIMARY, THEME_SECONDARY, THEME_ACCENT } = this.config
    const root = document.documentElement

    root.dataset.client = CLIENT
    root.style.setProperty('--app-theme-primary', THEME_PRIMARY)
    root.style.setProperty('--app-theme-secondary', THEME_SECONDARY)
    root.style.setProperty('--app-theme-accent', THEME_ACCENT)
    root.style.setProperty('--app-text-muted', '#6b7280')
    // Legacy vars used by Section, MFileInput, etc.
    root.style.setProperty('--primary-color', THEME_ACCENT)
    root.style.setProperty('--secondary-color', THEME_SECONDARY)
  }

  debug() {
    console.log('=== Config Debug ===')
    console.log('Env VITE_CLIENT:', import.meta.env.VITE_CLIENT)
    console.log('Env VITE_TITLE:', import.meta.env.VITE_TITLE)
    console.log('Env VITE_LOGO_TITLE:', import.meta.env.VITE_LOGO_TITLE)
    console.log('Config:', this.config)
  }
}

export const configService = new ConfigService()
